import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

const MAX_HEIGHT_VIEWPORT_OFFSET_PX = 80;

interface UseResizableHeightOptions {
  minHeight: number;
  onCommit?: (height: number) => void;
}

interface ResizeHandlePointerHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
}

interface UseResizableHeightReturn {
  height: number;
  dragHandleProps: ResizeHandlePointerHandlers;
}

/**
 * Drives a drag-to-resize handle that grows/shrinks an element anchored to the
 * viewport bottom. Height is measured from the pointer's distance to the bottom
 * edge and clamped between `minHeight` and the viewport height. `onCommit` fires
 * once on pointer release so callers can persist the final height.
 */
export function useResizableHeight(
  initialHeight: number,
  { minHeight, onCommit }: UseResizableHeightOptions
): UseResizableHeightReturn {
  const [height, setHeight] = useState(initialHeight);
  const heightRef = useRef(initialHeight);
  const isDraggingRef = useRef(false);

  const onPointerDown = useCallback((event: ReactPointerEvent): void => {
    isDraggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent): void => {
      if (!isDraggingRef.current) return;
      const maxHeight = window.innerHeight - MAX_HEIGHT_VIEWPORT_OFFSET_PX;
      const distanceFromBottom = window.innerHeight - event.clientY;
      const nextHeight = Math.min(Math.max(distanceFromBottom, minHeight), maxHeight);
      heightRef.current = nextHeight;
      setHeight(nextHeight);
    },
    [minHeight]
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent): void => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      event.currentTarget.releasePointerCapture(event.pointerId);
      onCommit?.(heightRef.current);
    },
    [onCommit]
  );

  return { height, dragHandleProps: { onPointerDown, onPointerMove, onPointerUp } };
}
